import { useEffect, useRef, useState } from 'react'
import { CheckCircleIcon } from '@solar-icons/react/linear/check-circle'
import { CopyIcon } from '@solar-icons/react/linear/copy'
import { Button } from './Button'
import type { ButtonProps } from './Button'
import { copyText } from '@/lib/utils/clipboard'

export interface CopyButtonProps extends Omit<ButtonProps, 'onClick' | 'icon' | 'value'> {
  /** A function defers building the string until the click. */
  value: string | (() => string)
  label?: string
  copiedLabel?: string
}

export function CopyButton({
  value,
  label = 'Copy',
  copiedLabel = 'Copied',
  variant = 'ghost',
  size = 'sm',
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  const handleClick = async () => {
    const text = typeof value === 'function' ? value() : value
    const ok = await copyText(text)
    if (!ok) return

    setCopied(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopied(false), 1600)
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={() => void handleClick()}
      icon={
        copied ? (
          <CheckCircleIcon size={14} strokeWidth={2} />
        ) : (
          <CopyIcon size={14} strokeWidth={1.75} />
        )
      }
      {...props}
    >
      {copied ? copiedLabel : label}
    </Button>
  )
}
